import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import VidInterviewerLogo from "../../../assets/images/VidInterviewerLogo.png";
import {
  CursorArrowRaysIcon,
  SparklesIcon,
  XMarkIcon,
  // CurrencyDollarIcon,
} from "@heroicons/react/24/outline";

const Header = ({ mainText, cornerText = "", showLandingNav = true }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      // Update active section based on scroll position
      const sections = ["our-process", "features", "pricing"];
      const currentSection = sections.find((section) => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      setActiveSection(currentSection || "");
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const mobileMenu = document.getElementById("mobile-menu");
      const mobileMenuButton = document.getElementById("mobile-menu-button");
      if (
        mobileMenu &&
        !mobileMenu.contains(event.target) &&
        !mobileMenuButton?.contains(event.target)
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setIsMobileMenuOpen(false);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
      setIsMobileMenuOpen(false);
    }
  };

  const navItems = [
    { id: "our-process", icon: CursorArrowRaysIcon, label: "Process" },
    { id: "features", icon: SparklesIcon, label: "Features" },
    // { id: "pricing", icon: CurrencyDollarIcon, label: "Pricing" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-white/90 backdrop-blur-md shadow-lg" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo and Title */}
          <div
            className="flex items-center cursor-pointer hover:opacity-80 transition-opacity"
            onClick={scrollToTop}
          >
            <img
              src={VidInterviewerLogo}
              alt="VidInterviewer Logo"
              className="w-12 h-12 object-contain"
            />
            <h2 className="ml-3 text-xl font-bold bg-clip-text text-transparent bg-gradient-primary font-sans">
              VidInterviewer
            </h2>
          </div>

          {/* Navigation */}
          {showLandingNav && (
            <nav className="hidden md:flex items-center space-x-8">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() =>
                    item.id ? scrollToSection(item.id) : scrollToTop()
                  }
                  className={`flex items-center space-x-2 px-3 py-2 rounded-full transition-all duration-300 ${
                    activeSection === item.id
                      ? "bg-peachYellow/20 text-ashGray"
                      : "text-ashGray/70 hover:text-ashGray hover:bg-peachYellow/10"
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              ))}
              <button
                onClick={() => scrollToSection("pricing")}
                className="ml-4 px-4 py-2 bg-gradient-to-r from-peachYellow to-coralRed text-white rounded-full font-medium hover:opacity-90 transition-opacity"
              >
                Get Started
              </button>
            </nav>
          )}

          {/* Mobile menu button */}
          <button
            id="mobile-menu-button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-md text-ashGray hover:bg-peachYellow/10"
          >
            {isMobileMenuOpen ? (
              <XMarkIcon className="w-6 h-6" />
            ) : (
              <svg
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {showLandingNav && (
        <div
          id="mobile-menu"
          className={`fixed top-20 right-0 w-64 h-screen bg-white shadow-lg transform transition-transform duration-300 ease-in-out z-50 ${
            isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <nav className="flex flex-col p-4 space-y-4">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() =>
                  item.id ? scrollToSection(item.id) : scrollToTop()
                }
                className={`flex items-center space-x-2 px-3 py-2 rounded-full transition-all duration-300 ${
                  activeSection === item.id
                    ? "bg-peachYellow/20 text-ashGray"
                    : "text-ashGray/70 hover:text-ashGray hover:bg-peachYellow/10"
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </button>
            ))}
            <button
              onClick={() => scrollToSection("pricing")}
              className="mt-4 px-4 py-2 bg-gradient-to-r from-peachYellow to-coralRed text-white rounded-full font-medium hover:opacity-90 transition-opacity"
            >
              Get Started
            </button>
          </nav>
        </div>
      )}

      {/* Main Text (only shown when not scrolled) */}
      {!isScrolled && mainText && (
        <div className="absolute left-0 right-0 bottom-0 h-20 flex items-center justify-center">
          <h1 className="text-md sm:text-xl md:text-2xl font-bold font-sans text-center text-ashGray">
            {mainText}
          </h1>
        </div>
      )}
    </header>
  );
};

Header.propTypes = {
  mainText: PropTypes.string.isRequired,
  cornerText: PropTypes.string,
  showLandingNav: PropTypes.bool,
};

export default Header;
